import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';

import { axiosPrivate } from 'app/utils';

import type { PlusWebsitesResponse } from '../../../hooks/usePlusWebsites';

interface UpdateWebsiteDeploymentParams {
  websiteId: string;
  isDeployed: boolean;
}

function updateWebsiteDeployment({ websiteId, isDeployed }: UpdateWebsiteDeploymentParams) {
  return axiosPrivate
    .put(`/v3/support/plus-websites/${websiteId}/deployments`, { isDeployed })
    .then((r) => r.data);
}

export function useUpdateWebsiteDeployment() {
  const queryClient = useQueryClient();
  const queryKey = ['plus-websites'];

  return useMutation({
    mutationFn: updateWebsiteDeployment,
    // Optimistic write with rollback on error. Deliberately no invalidation on
    // settle: the list endpoint can lag behind this PUT, so an immediate refetch
    // may return the old deployment state and snap the toggle back.
    onMutate: async ({ websiteId, isDeployed }: UpdateWebsiteDeploymentParams) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueriesData<AxiosResponse<PlusWebsitesResponse>>({
        queryKey,
      });

      queryClient.setQueriesData<AxiosResponse<PlusWebsitesResponse>>({ queryKey }, (old) =>
        old
          ? {
              ...old,
              data: {
                ...old.data,
                plusWebsites: old.data.plusWebsites.map((website) =>
                  website.id === websiteId ? { ...website, isDeployed } : website
                ),
              },
            }
          : old
      );

      return { previous };
    },
    onError: (_error, _params, context) => {
      context?.previous?.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
    },
    meta: {
      successMessage: 'Website status updated successfully',
      errorMessage: 'Failed to update website status',
    },
  });
}
